import type { AgentRunListItem } from '@fos/shared/contracts/agents';
import { AGENT_RUN_COST_INFORMATIONAL_LABEL, agentExecutorLabel } from '@fos/shared/labels';
import { ChevronRight } from 'lucide-react';
import { Link, useNavigate } from 'react-router';
import { formatRelativePast } from '../../../lib/format';
import { Mono, Table, TBody, Td, THead, Th, Tr } from '../../../ui';
import { StatusPill } from '../../../ui/StatusPill';
import { formatRunCost, formatRunDuration, runContext, runPath, runStatusLabel, runStatusTone } from '../format';

/**
 * Koszt w liście: koszt informacyjny (Claude Code CLI przy subskrypcji, Fala 6) wyciszony, z opisem w podpowiedzi
 * i dla czytnika ekranu — nie liczy się do budżetu AI.
 */
function RunCost({ run, className }: { run: AgentRunListItem; className: string }) {
  if (!run.costInformational) return <Mono className={className}>{formatRunCost(run.costPln)}</Mono>;
  return (
    <Mono className={`${className} text-fg-muted`} title={AGENT_RUN_COST_INFORMATIONAL_LABEL}>
      {formatRunCost(run.costPln)}
      <span className="sr-only">, {AGENT_RUN_COST_INFORMATIONAL_LABEL}</span>
    </Mono>
  );
}

/**
 * „Ostatnie przebiegi” (Figma 11): desktop — tabela (zadanie z klientem, wykonawca, czas, koszt, status z kropką,
 * strzałka), mobile (< 768 px) — wiersze bez kart z tymi samymi danymi. Koszt tylko z „Widzi stawki i kwoty”.
 */
export function RunsList({ items, showCost }: { items: AgentRunListItem[]; showCost: boolean }) {
  const navigate = useNavigate();
  return (
    <>
      <div className="hidden md:block">
        <Table aria-label="Ostatnie przebiegi">
          <THead>
            <Tr>
              <Th>Zadanie</Th>
              <Th>Wykonawca</Th>
              <Th align="right">Czas</Th>
              {showCost && <Th align="right">Koszt</Th>}
              <Th className="pl-4">Status</Th>
              <Th>
                <span className="sr-only">Otwórz</span>
              </Th>
            </Tr>
          </THead>
          <TBody>
            {items.map((run) => (
              <Tr
                key={run.id}
                className="cursor-pointer transition-colors hover:bg-surface"
                onClick={() => navigate(runPath(run.id))}
              >
                <Td className="max-w-0 w-[45%]">
                  <Link
                    to={runPath(run.id)}
                    onClick={(e) => e.stopPropagation()}
                    className="block truncate text-body-s text-fg focus-visible:outline-2 focus-visible:outline-accent"
                  >
                    {run.title}
                  </Link>
                  {runContext(run) && (
                    <span className="block truncate text-caption text-fg-muted">{runContext(run)}</span>
                  )}
                </Td>
                <Td className="text-fg-secondary whitespace-nowrap">{agentExecutorLabel(run)}</Td>
                <Td align="right">
                  <Mono className="text-caption text-fg-secondary">{formatRunDuration(run.durationSec)}</Mono>
                </Td>
                {showCost && (
                  <Td align="right">
                    <RunCost run={run} className="text-caption font-medium text-fg" />
                  </Td>
                )}
                <Td className="pl-4">
                  <StatusPill tone={runStatusTone(run)}>{runStatusLabel(run)}</StatusPill>
                </Td>
                <Td align="right" className="w-8">
                  <ChevronRight size={16} strokeWidth={1.75} aria-hidden className="text-fg-muted" />
                </Td>
              </Tr>
            ))}
          </TBody>
        </Table>
      </div>
      <ul className="md:hidden" aria-label="Ostatnie przebiegi">
        {items.map((run) => (
          <li key={run.id} className="border-b border-line">
            <Link
              to={runPath(run.id)}
              className="flex min-h-[60px] items-center gap-3 py-3 focus-visible:outline-2 focus-visible:outline-accent"
            >
              <span className="flex min-w-0 flex-1 flex-col gap-0.5">
                <span className="line-clamp-2 text-body-s break-words text-fg">{run.title}</span>
                <span className="truncate text-caption text-fg-muted">
                  {[runContext(run), agentExecutorLabel(run), formatRelativePast(run.createdAt)]
                    .filter(Boolean)
                    .join(' · ')}
                </span>
                <StatusPill tone={runStatusTone(run)} size="s" className="mt-1 self-start">
                  {runStatusLabel(run)}
                </StatusPill>
              </span>
              <span className="flex shrink-0 flex-col items-end gap-0.5">
                <Mono className="text-caption text-fg-secondary">{formatRunDuration(run.durationSec)}</Mono>
                {showCost && <RunCost run={run} className="text-caption text-fg" />}
              </span>
              <ChevronRight size={16} strokeWidth={1.75} aria-hidden className="shrink-0 text-fg-muted" />
            </Link>
          </li>
        ))}
      </ul>
    </>
  );
}
